import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Camera, X, Download, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PackagePhotosDialog = ({ open, onOpenChange, packageId, oceCode }) => {
  const { t } = useLanguage();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewingIndex, setViewingIndex] = useState(null);

  useEffect(() => {
    if (open && packageId) {
      fetchAttachments();
    }
    if (!open) {
      setViewingIndex(null);
    }
  }, [open, packageId]);

  const fetchAttachments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/packages/${packageId}/attachments`);
      const images = res.data.filter(
        (att) => att.file_type === 'image' || att.file_type === 'IMAGE'
      );
      setAttachments(images);
    } catch {
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  };

  const getImageSrc = (att) => {
    // S3/CloudFront: file_path is a full URL — use directly
    if (att.file_path?.startsWith('https://')) {
      return att.file_path;
    }
    // Local storage fallback: use download endpoint
    return `${API}/attachments/${att.id}/download`;
  };

  const handleDownload = (attachment) => {
    const url = getImageSrc(attachment);
    const a = document.createElement('a');
    a.href = url;
    a.download = attachment.filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const viewing = viewingIndex !== null ? attachments[viewingIndex] : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="h-5 w-5" />
            {t('photos')} — {oceCode}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : attachments.length === 0 ? (
          <div className="text-center py-12">
            <Camera className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">{t('noPhotos')}</p>
          </div>
        ) : viewingIndex !== null && viewing ? (
          /* Full-size viewer */
          <div>
            <div className="flex items-center justify-between mb-3">
              <Badge variant="secondary">{viewingIndex + 1} / {attachments.length}</Badge>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDownload(viewing)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  {t('download')}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setViewingIndex(null)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="relative bg-muted rounded-lg overflow-hidden flex items-center justify-center min-h-[300px]">
              <img
                src={getImageSrc(viewing)}
                alt={viewing.filename}
                className="max-w-full max-h-[60vh] object-contain"
              />
              {attachments.length > 1 && (
                <>
                  <button
                    onClick={() => setViewingIndex((viewingIndex - 1 + attachments.length) % attachments.length)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setViewingIndex((viewingIndex + 1) % attachments.length)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2 text-center truncate">{viewing.filename}</p>
          </div>
        ) : (
          /* Grid view */
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {attachments.map((att, i) => (
              <div
                key={att.id}
                className="relative group cursor-pointer rounded-lg overflow-hidden border hover:border-blue-400 transition-colors"
                onClick={() => setViewingIndex(i)}
              >
                <img
                  src={getImageSrc(att)}
                  alt={att.filename}
                  className="w-full h-32 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-end">
                  <p className="text-xs text-white bg-black/50 w-full px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                    {att.filename}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PackagePhotosDialog;
